/**
 * LE TRAJET ROUTIER entre un formateur et le point de référence, pour la bulle
 * de la carte des formateurs.
 *
 * La distance que le serveur calcule est à vol d'oiseau : elle trie et filtre
 * bien, mais elle ment sur ce qu'un formateur fera vraiment — trente kilomètres
 * de part et d'autre d'un massif ne se font pas en trente kilomètres.
 *
 * PAR OSRM, ET NON PAR GOOGLE. Le calcul passe par le serveur public de
 * `project-osrm.org` : libre, sans clé, sans facture. La GMAO (autre dépôt)
 * s'en sert déjà pour ses interventions, c'est donc un choix déjà fait dans la
 * maison. Son origine est autorisée par la CSP (`server/index.js`,
 * `connectSrc`) : sans elle, tout échouerait en production. Distance Matrix, chez Google, aurait facturé chaque bulle
 * ouverte (~5 $ les mille) pour un renseignement qu'on lit en passant.
 *
 * C'est un serveur de COURTOISIE : on ne l'appelle qu'à l'ouverture d'une bulle
 * — jamais pour les mille points d'une carte —, on garde ses réponses pour la
 * durée de la page, et deux ouvertures rapprochées du même point partagent la
 * même requête. Une réponse qui tarde est abandonnée au bout de huit secondes.
 *
 * Tout échec retombe sur la distance à vol d'oiseau, dite comme telle par la
 * bulle (cf. `FormateursMap.secoursVolDOiseau`).
 */

/** Au-delà, on lâche : la bulle ne doit pas rester sur « Calcul du trajet… ». */
const DELAI_MAX_MS = 8000

const OSRM = 'https://router.project-osrm.org/route/v1/driving'

const cache = new Map()
const enVol = new Map()

const cleTrajet = (depart, arrivee) => `${depart.lat},${depart.lon}|${arrivee.lat},${arrivee.lon}`

/**
 * « 42 km · 38 min » par la route, ou rejette si OSRM ne rend pas d'itinéraire.
 *
 * @param {{lat:number, lon:number}} depart  la commune du formateur
 * @param {{lat:number, lon:number}} arrivee le point de référence
 */
export function trajetRoutier(depart, arrivee) {
  const cle = cleTrajet(depart, arrivee)
  if (cache.has(cle)) return Promise.resolve(cache.get(cle))
  if (enVol.has(cle)) return enVol.get(cle)

  const promesse = demander(depart, arrivee)
    .then((texte) => {
      cache.set(cle, texte)
      return texte
    })
    .finally(() => enVol.delete(cle))

  enVol.set(cle, promesse)
  return promesse
}

async function demander(depart, arrivee) {
  const abandon = new AbortController()
  const minuterie = setTimeout(() => abandon.abort(), DELAI_MAX_MS)
  try {
    const reponse = await fetch(
      `${OSRM}/${depart.lon},${depart.lat};${arrivee.lon},${arrivee.lat}?overview=false`,
      { signal: abandon.signal }
    )
    if (!reponse.ok) throw new Error(`OSRM ${reponse.status}`)

    const donnees = await reponse.json()
    const route = donnees?.routes?.[0]
    if (donnees?.code !== 'Ok' || route?.distance == null) throw new Error(donnees?.code || 'SANS_ITINERAIRE')

    return `${formatKm(route.distance / 1000)} · ${formatDuree(route.duration / 60)}`
  } finally {
    clearTimeout(minuterie)
  }
}

/** « 4,4 km » sous dix kilomètres, « 42 km » au-delà : la précision suit l'échelle. */
export function formatKm(km) {
  const arrondi = km < 10 ? Math.round(km * 10) / 10 : Math.round(km)
  return `${arrondi.toLocaleString('fr-FR')} km`
}

/** « 17 min », « 1 h 12 » : une durée de trajet ne se lit pas en minutes au-delà de l'heure. */
export function formatDuree(minutes) {
  const total = Math.max(1, Math.round(minutes))
  if (total < 60) return `${total} min`
  const heures = Math.floor(total / 60)
  const reste = total % 60
  return reste ? `${heures} h ${String(reste).padStart(2, '0')}` : `${heures} h`
}

/** Vide la mémoire des trajets — pour les tests. */
export function oublierTrajets() {
  cache.clear()
  enVol.clear()
}
